import { createSlice } from "@reduxjs/toolkit";
import {
  prepareVideoRoom,
  leaveVideoRoom,
  deleteVideoRoom,
} from "../services/videoCall";
import {
  startInterpreterActivity,
  completeInterpreterActivity,
  completeAppointment,
} from "../services/appointments";

import {
  DISCONNECTED,
  NOT_STARTED,
  PREPARING,
  READY,
  JOINING,
  CONNECTED,
  LEAVING,
  ENDED,
} from "../constants/videoCallStatus";

import { extractErrorMessage } from "../lib/utils";

const initialState = {
  status: NOT_STARTED,
  isLoading: false,
  error: null,
  appointment: null,
  identity: null,
  room: null,
  token: null,
  remoteParticipants: [],
  exitedParticipant: null,
  allowedActions: [],
  activityId: null,
  meetingEnded: false,
};

const video = createSlice({
  name: "videoCall",
  initialState,
  reducers: {
    prepareStarted: (state) => {
      state.isLoading = true;
      state.status = PREPARING;
    },
    prepareFulfilled: (state, action) => {
      const {
        appointment,
        identity,
        room,
        token,
        participants,
        allowed,
      } = action.payload;
      state.isLoading = false;
      state.error = null;
      state.status = READY;
      state.appointment = appointment;
      state.identity = identity;
      state.room = room;
      state.token = token;
      state.remoteParticipants = participants;
      state.exitedParticipant = null;
      state.allowedActions = allowed;
    },
    prepareRejected: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
      state.status = NOT_STARTED;
    },
    joinStarted: (state) => {
      state.status = JOINING;
      state.isLoading = true;
    },
    joinFulfilled: (state) => {
      state.isLoading = false;
      state.status = CONNECTED;
      state.error = null;
    },
    joinRejected: (state, action) => {
      state.isLoading = false;
      state.status = DISCONNECTED;
      state.error = action.payload;
    },
    remoteParticipantJoined: (state, action) => {
      const { sid } = action.payload;
      const exist = state.remoteParticipants.find((p) => p.sid === sid);
      if (!exist) {
        state.remoteParticipants.push(action.payload);
      }
    },
    remoteParticipantLeft: (state, action) => {
      const { sid } = action.payload;
      state.remoteParticipants = state.remoteParticipants.filter(
        (p) => p.sid !== sid
      );
    },
    leaveStarted: (state) => {
      state.status = LEAVING;
      state.isLoading = true;
    },
    leaveFulfilled: (state) => {
      // if this action was called without `leaveStarted`, that means, meeting was ended by
      // other participants.
      if (state.status === CONNECTED) {
        state.meetingEnded = true;
      }
      state.isLoading = false;
      state.error = null;
      state.status = ENDED;
      state.token = null;
    },
    leaveRejected: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    endStarted: (state) => {
      state.status = LEAVING;
      state.isLoading = true;
    },
    endFulfilled: (state, action) => {
      state.isLoading = false;
      state.error = null;
      state.status = ENDED;
      state.token = null;
      state.room = null;
      state.meetingEnded = true;
    },
    endRejected: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    activityCreationStated: (state) => {
      state.isLoading = true;
    },
    activityCreationFulfilled: (state, action) => {
      const { activityId } = action.payload;
      state.isLoading = false;
      state.activityId = activityId;
    },
    activityCreationRejected: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    activityFinishStated: (state) => {
      state.isLoading = true;
    },
    activityFinishFulfilled: (state, action) => {
      state.isLoading = false;
      state.activityId = null;
    },
    activityFinishRejected: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },
  },
});

const prepare = (appointmentId, authKey) => (dispatch) => {
  const { prepareStarted, prepareFulfilled, prepareRejected } = video.actions;

  dispatch(prepareStarted());
  prepareVideoRoom(appointmentId, authKey)
    .then((res) => dispatch(prepareFulfilled(res)))
    .catch((err) => {
      const message = extractErrorMessage(err);
      dispatch(prepareRejected(message));
    });
};

const leave = (appointmentId, authKey) => (dispatch) => {
  const { leaveStarted, leaveFulfilled, leaveRejected } = video.actions;
  dispatch(leaveStarted());
  leaveVideoRoom(appointmentId, authKey)
    .then((res) => dispatch(leaveFulfilled(res)))
    .catch((err) => {
      const message = extractErrorMessage(err);
      dispatch(leaveRejected(message));
    });
};

const terminate = (appointmentId) => async (dispatch) => {
  const { endStarted, endFulfilled, endRejected } = video.actions;
  dispatch(endStarted());
  try {
    await completeAppointment(appointmentId);
    await deleteVideoRoom(appointmentId);
    dispatch(endFulfilled());
  } catch (err) {
    const message = extractErrorMessage(err);
    dispatch(endRejected(message));
  }
};

// createInterpreterActivity Thunk
const createInterpreterActivity = (apptId, activityType) => async (
  dispatch
) => {
  const {
    activityCreationStated,
    activityCreationFulfilled,
    activityCreationRejected,
  } = video.actions;

  console.log("#############################################");
  console.log(
    "Interpreter Activity Creating...",
    apptId,
    activityType,
    new Date()
  );
  console.log("#############################################");

  dispatch(activityCreationStated());
  startInterpreterActivity(apptId, activityType)
    .then((res) => dispatch(activityCreationFulfilled(res)))
    .catch((err) => {
      const message = extractErrorMessage(err);
      dispatch(activityCreationRejected(message));
      throw err;
    });
};

// finishInterpreterActivity Thunk
const finishInterpreterActivity = (apptId) => async (dispatch, getState) => {
  const {
    activityFinishStated,
    activityFinishFulfilled,
    activityFinishRejected,
  } = video.actions;
  const { activityId } = getState()[video.name];

  if (activityId !== null) {
    console.log("#############################################");
    console.log(
      "Interpreter Activity Completing...",
      apptId,
      activityId,
      new Date()
    );
    console.log("#############################################");

    dispatch(activityFinishStated());
    completeInterpreterActivity(apptId, activityId)
      .then((res) => dispatch(activityFinishFulfilled(res)))
      .catch((err) => {
        const message = extractErrorMessage(err);
        dispatch(activityFinishRejected(message));
      });
  }
};

export const actions = {
  ...video.actions,
  prepare,
  leave,
  terminate,
  createInterpreterActivity,
  finishInterpreterActivity,
};

// selectors;
export const isLoading = (state) => state.isLoading;
export const getError = (state) => state.error;
export const getToken = (state) => state.token;
export const getRoom = (state) => state.room;
export const getAppointment = (state) => state.appointment;
export const getRemoteParticipants = (state) => state.remoteParticipants;
export const getExitedParticipant = (state) => state.exitedParticipant;
export const getCurrentStatus = (state) => state.status;
export const isCallReady = (state) => state.status === READY;
export const isCallEnded = (state) => state.status === ENDED;
export const isMeetingEnded = (state) => state.meetingEnded;
export const allowedActions = (state) => state.allowedActions;
export const getActivityId = (state) => state.activityId;

export default video.reducer;
